import {
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
} from "@mui/material";
import { DateTime } from "luxon";
import { Tasks } from "../models/tasks";

interface TasksWeekViewProps {
  periodTasks: Tasks[];
  onClickDate: (date: string) => void;
}

export function TasksWeekView({
  periodTasks,
  onClickDate,
}: TasksWeekViewProps) {
  return (
    <Box pt={2}>
      <Typography sx={{ float: "right" }} color="black" variant="body1" p={1}>
        Trays to Sow Total:{" "}
        {periodTasks.reduce(
          (o, tasks) => tasks.sow.reduce((p, task) => task.trays + p, 0) + o,
          0,
        )}
      </Typography>

      <Table size={"small"}>
        <TableHead>
          <TableRow>
            {periodTasks.map((tasks) => (
              <TableCell
                key={`header-${tasks.date}`}
                align="center"
                sx={{ width: "14.3%" }}
              >
                <Button
                  color="secondary"
                  variant="text"
                  onClick={() => onClickDate(tasks.date)}
                  sx={
                    DateTime.fromISO(tasks.date).equals(
                      DateTime.now().startOf("day"),
                    )
                      ? {
                          borderRadius: "50%",
                          backgroundColor: "primary.main",
                          color: "primary.contrastText",
                        }
                      : undefined
                  }
                >
                  <Typography
                    variant="caption"
                    sx={{
                      textDecoration: "underline",
                      color: "black",
                    }}
                  >
                    {DateTime.fromISO(tasks.date)
                      .toLocaleString({ weekday: "short" })
                      .replace(".", "")}
                    <br />
                    {DateTime.fromISO(tasks.date).toLocaleString({
                      day: "numeric",
                    })}
                  </Typography>
                </Button>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {/* Harvest row */}
          <TableRow sx={{ height: "80px" }}>
            {periodTasks.map((tasks) => (
              <TableCell key={`harvest-${tasks.date}`}>
                {tasks.harvest.length > 0 && (
                  <Box
                    sx={{
                      background: "#56d787",
                      minHeight: "60px",
                      padding: 1,
                      borderRadius: "5px",
                      textAlign: "center",
                    }}
                  >
                    <Typography variant={"caption"}>
                      Harvest
                      <br />
                      {tasks.harvest.reduce(
                        (total, task) => total + task.trays,
                        0,
                      )}{" "}
                      tray(s)
                    </Typography>
                  </Box>
                )}
              </TableCell>
            ))}
          </TableRow>

          {/* Soak row */}
          <TableRow sx={{ height: "80px" }}>
            {periodTasks.map((tasks) => (
              <TableCell key={`soak-${tasks.date}`}>
                {tasks.soak.length > 0 && (
                  <Box
                    sx={{
                      background: "#dab894",
                      minHeight: "60px",
                      padding: 1,
                      borderRadius: "5px",
                      textAlign: "center",
                    }}
                  >
                    <Typography variant={"caption"}>
                      Soak
                      <br />
                      {tasks.soak.reduce(
                        (total, task) => total + task.trays,
                        0,
                      )}{" "}
                      tray(s)
                    </Typography>
                  </Box>
                )}
              </TableCell>
            ))}
          </TableRow>

          {/* Sow row */}
          <TableRow sx={{ height: "80px" }}>
            {periodTasks.map((tasks) => (
              <TableCell key={`sow-${tasks.date}`}>
                {tasks.sow.length > 0 && (
                  <Box
                    sx={{
                      background: "#EAB159",
                      minHeight: "60px",
                      padding: 1,
                      borderRadius: "5px",
                      textAlign: "center",
                    }}
                  >
                    <Typography variant={"caption"}>
                      Sow
                      <br />
                      {tasks.sow.reduce(
                        (total, task) => total + task.trays,
                        0,
                      )}{" "}
                      tray(s)
                    </Typography>
                  </Box>
                )}
              </TableCell>
            ))}
          </TableRow>

          {/* Blackout row */}
          <TableRow sx={{ height: "80px" }}>
            {periodTasks.map((tasks) => (
              <TableCell key={`blackout-${tasks.date}`}>
                {tasks.blackout.length > 0 && (
                  <Box
                    sx={{
                      background: "#FFF1AB",
                      minHeight: "60px",
                      padding: 1,
                      borderRadius: "5px",
                      textAlign: "center",
                    }}
                  >
                    <Typography variant={"caption"}>
                      Blackout
                      <br />
                      {tasks.blackout.reduce(
                        (total, task) => total + task.trays,
                        0,
                      )}{" "}
                      tray(s)
                    </Typography>
                  </Box>
                )}
              </TableCell>
            ))}
          </TableRow>

          {/* Uncover row */}
          <TableRow sx={{ height: "80px" }}>
            {periodTasks.map((tasks) => (
              <TableCell key={`uncover-${tasks.date}`}>
                {tasks.uncover.length > 0 && (
                  <Box
                    sx={{
                      background: "#ffee93",
                      minHeight: "60px",
                      padding: 1,
                      borderRadius: "5px",
                      textAlign: "center",
                    }}
                  >
                    <Typography variant={"caption"}>
                      Uncover
                      <br />
                      {tasks.uncover.reduce(
                        (total, task) => total + task.trays,
                        0,
                      )}{" "}
                      tray(s)
                    </Typography>
                  </Box>
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableBody>
      </Table>
    </Box>
  );
}
